// Pure helpers for the platform request board.
//
// A plain .ts module with no JSX and no imports, so Node's own test runner can
// check it under --experimental-strip-types. The things that are easy to get
// wrong here are all sentences about honesty: whether a date is measured or
// guessed, and whether a clock has run out. Those deserve a test, and a test is
// only possible if they are not inside a component.

export type Band = {
  severity: string
  label: string
  rank: number
  days_to_fix: number
  provisional: boolean
  calibrated_from_n: number
  effective_from?: string | null
  note?: string | null
  set_by?: string | null
  id?: string
}

export type Proposal = {
  severity: string
  label: string
  rank: number
  in_force_days: number
  in_force_provisional: boolean
  n_closed: number
  measured_median_days: number | null
  measured_p90_days: number | null
  measured_max_days: number | null
  n_over_band: number
}

export type PlatformRequest = {
  id: string
  ref: number
  status: string
  kind: string | null
  area: string | null
  severity: string | null
  severity_label: string | null
  severity_rank: number | null
  band_days: number | null
  band_provisional: boolean | null
  band_calibrated_from_n: number | null
  title: string | null
  reported_text: string
  diagnosis: string | null
  suspected_cause: string | null
  proposed_effort_days: number | null
  triage_source: string | null
  model_reading: ModelReading | null
  model_read_at: string | null
  model_name: string | null
  source: string
  page_path: string | null
  source_meeting: string | null
  reporter: string
  reporter_name: string | null
  reported_at: string
  reported_on: string
  triaged_at: string | null
  triaged_by: string | null
  promise_by: string | null
  accepted_at: string | null
  assignee_email: string | null
  assignee_name: string | null
  dev_plan_ref: string | null
  task_id: string | null
  task_ref: number | null
  resolved_at: string | null
  resolved_by: string | null
  resolution: string | null
  declined_at: string | null
  declined_by: string | null
  decline_reason: string | null
  elapsed_days: number
  days_to_promise: number | null
  is_overdue: boolean
  missed_promise: boolean | null
  triage_due_at: string | null
  triage_overdue: boolean
  questions_asked: number
  questions_answered: number
  asset_count: number
}

export type ModelReading = {
  kind?: string | null
  area?: string | null
  severity?: string | null
  title?: string | null
  diagnosis?: string | null
  suspected_cause?: string | null
  effort_days?: number | null
  confidence?: string | null
  attachment_problems?: string[] | null
  images_read?: number | null
}

export const KINDS = ['bug', 'feature', 'question'] as const

/**
 * How a promised date should be described, and this is the honest core of the
 * whole screen.
 *
 * A band that is still provisional produces "provisional", not "due". On
 * 2 September 2026 all five were, because there was no cycle-time history to
 * calibrate against: 200 of the 347 tasks marked complete on the task board have
 * a completion date earlier than their creation date, because they were imported
 * from monday.com, and only 12 human-created tasks have ever been finished here.
 *
 * The word matters more than it looks. "Due 4 September" tells the person who
 * reported a problem that somebody has worked out how long it takes. "Provisional,
 * 4 September" tells them the truth, which is that a number was chosen and is
 * being measured. Fleetly's whole claim to a driver is that the date and the
 * figure it gives him are real, and a board that trained the team to read a
 * Fleetly date as approximate would cost more than it saves.
 */
export function promiseLabel(r: {
  promise_by: string | null
  band_provisional: boolean | null
}): string {
  if (!r.promise_by) return 'No date yet'
  return r.band_provisional === false ? `Due ${r.promise_by}` : `Provisional, ${r.promise_by}`
}

/** The one-line state of a report's two clocks, for a badge. */
export function clockLabel(r: {
  status: string
  is_overdue: boolean
  triage_overdue: boolean
  days_to_promise: number | null
  elapsed_days: number
  missed_promise: boolean | null
}): { text: string; tone: 'ok' | 'warn' | 'bad' | 'muted' } {
  if (r.status === 'Declined') return { text: 'Declined', tone: 'muted' }
  if (r.status === 'Resolved') {
    return r.missed_promise
      ? { text: `Closed late, ${r.elapsed_days} days`, tone: 'warn' }
      : { text: `Closed in ${r.elapsed_days} days`, tone: 'ok' }
  }
  if (r.is_overdue) {
    const over = r.days_to_promise === null ? null : Math.abs(r.days_to_promise)
    return { text: over === null ? 'Overdue' : `${over} days overdue`, tone: 'bad' }
  }
  // Said before the fix clock, because "nobody has read this yet" is a different
  // failure from "this is hard" and it is the one a reporter feels first.
  if (r.triage_overdue) return { text: 'Not yet read', tone: 'warn' }
  if (r.days_to_promise === null) return { text: `Open ${r.elapsed_days} days`, tone: 'muted' }
  if (r.days_to_promise === 0) return { text: 'Due today', tone: 'warn' }
  return { text: `${r.days_to_promise} days left`, tone: 'ok' }
}

/**
 * Whether a measured proposal is worth acting on yet, and the sentence to show.
 *
 * Twenty is not a statistical threshold, it is a judgement, and it is stated as
 * one. Below it the median of a handful of closed reports is a number and it is
 * not evidence, and the screen says so rather than hiding the row: hiding it
 * would mean nobody could see the measurement accumulating.
 */
export const CALIBRATION_MINIMUM = 20

export function proposalNote(p: Proposal): string {
  if (p.n_closed === 0) return 'Nothing closed yet, so there is nothing to measure.'
  if (p.n_closed < CALIBRATION_MINIMUM) {
    return `${p.n_closed} closed so far. Too few to replace the band, but it is being counted.`
  }
  const median = p.measured_median_days ?? 0
  const direction =
    median > p.in_force_days ? 'longer than' : median < p.in_force_days ? 'shorter than' : 'the same as'
  return `${p.n_closed} closed. Median ${median} days, ${direction} the ${p.in_force_days} promised. ${p.n_over_band} went over.`
}

/** Ready to replace a guess with a measurement. */
export function canCalibrate(p: Proposal): boolean {
  return p.n_closed >= CALIBRATION_MINIMUM && p.measured_median_days !== null
}

/**
 * The reading and the confirmed triage, compared.
 *
 * Returned as a list of differences rather than a boolean, because the point is
 * to be able to say WHICH field the model got wrong. Over enough reports that
 * list is the only evidence anyone will have about whether the reading is worth
 * trusting, and a boolean would throw it away.
 */
export function readingDisagreements(r: PlatformRequest): string[] {
  const m = r.model_reading
  if (!m || !r.triaged_at) return []
  const out: string[] = []
  if (m.kind && r.kind && m.kind !== r.kind) out.push(`kind: read as ${m.kind}, recorded as ${r.kind}`)
  if (m.severity && r.severity && m.severity !== r.severity) {
    out.push(`severity: read as ${m.severity}, recorded as ${r.severity}`)
  }
  if (m.area && r.area && m.area !== r.area) out.push(`area: read as ${m.area}, recorded as ${r.area}`)
  return out
}

/** Board sections, in the order a person works through them. */
export const STATUS_ORDER = [
  'Awaiting triage',
  'Clarifying',
  'Triaged',
  'Accepted',
  'Resolved',
  'Declined',
] as const

export function groupByStatus(rows: PlatformRequest[]): Array<[string, PlatformRequest[]]> {
  const out: Array<[string, PlatformRequest[]]> = []
  for (const status of STATUS_ORDER) {
    const group = rows.filter((r) => r.status === status)
    if (group.length > 0) out.push([status, group])
  }
  // Anything with a status this file does not know about is shown rather than
  // silently dropped. A row that vanishes from a board because the code has not
  // been taught its status is the worst failure a board can have.
  const known = new Set<string>(STATUS_ORDER)
  const rest = rows.filter((r) => !known.has(r.status))
  if (rest.length > 0) out.push(['Other', rest])
  return out
}

export function fileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
